import { useMemo, useState } from "react";
import { ReactComponent as Vector9Icon } from "../../assets/images/vector_arrow.svg";
import {
  TemplateRowCard,
  TEMPLATE_SUMMARY_BODY_COLUMNS,
  AttachmentRowCard,
} from "../../ui/cards/TemplateCards";
import { formatFileSize } from "../../utils/fileUtils";
import styles from "../../styles/components/templates/TemplatesTable.module.css";

function getOwnerName(row) {
  return row?.responsible?.name?.trim() || "-";
}

function getTemplateName(row) {
  return row?.group?.name ?? row?.name ?? "";
}

function getTemplateFilesCount(row) {
  if (typeof row?.max_number === "number") return row.max_number;
  if (typeof row?.template_files_count === "number") {
    return row.template_files_count;
  }
  return 0;
}

function getTemplateValue(row, key) {
  const values = {
    template: getTemplateName(row),
    account: row?.account?.name ?? "",
    files: getTemplateFilesCount(row),
    responsible: getOwnerName(row),
  };

  return values[key] ?? "";
}

function getAttachmentValue(row, key) {
  const values = {
    template: row?.name ?? "",
    account: row?.account?.name ?? "",
    size: row?.size ?? 0,
  };

  return values[key] ?? "";
}

const headerCellStyle = {
  textAlign: "center",
  color: "var(--color-text-text-primary)",
  fontSize: 14,
  fontFamily: "DM Sans",
  fontWeight: 500,
  lineHeight: "17px",
  minWidth: 0,
  whiteSpace: "nowrap",
};

function HeaderCell({
  children,
  column,
  sortKey,
  sortConfig,
  onSort,
  align = "center",
}) {
  const isActive = sortConfig.key === sortKey;
  const directionLabel = sortConfig.direction === "asc" ? "↑" : "↓";

  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      style={{
        ...headerCellStyle,
        gridColumn: column,
        textAlign: align,
        background: "transparent",
        border: "none",
        cursor: "pointer",
      }}
    >
      {children} {isActive ? directionLabel : ""}
    </button>
  );
}

function HeaderLabel({ children, column, align = "center" }) {
  return (
    <div style={{ ...headerCellStyle, gridColumn: column, textAlign: align }}>
      {children}
    </div>
  );
}

let accountTextMeasurementContext;

function getAccountTextMeasurementContext() {
  if (accountTextMeasurementContext !== undefined) {
    return accountTextMeasurementContext;
  }

  try {
    accountTextMeasurementContext =
      document.createElement("canvas").getContext("2d") ?? null;
  } catch {
    accountTextMeasurementContext = null;
  }

  return accountTextMeasurementContext;
}

function measureAccountTextWidth(accountName) {
  const context = getAccountTextMeasurementContext();

  if (!context) return accountName.length * 8.5;

  context.font = "500 13px 'DM Sans', sans-serif";
  return context.measureText(accountName).width;
}

function getAttachmentColumns(rows) {
  const widestAccountText = rows.reduce((widest, row) => {
    const accountName = row?.account?.name ?? "";
    if (!accountName) return widest;
    return Math.max(widest, measureAccountTextWidth(accountName));
  }, 0);

  const accountColumnWidth = widestAccountText
    ? Math.min(Math.round(widestAccountText) + 64, 640)
    : 96;

  return `64px minmax(0, 1fr) ${accountColumnWidth}px minmax(32px, 96px) 120px minmax(32px, 96px) 160px`;
}

function TemplatesTable({
  rows = [],
  onSelectTemplate,
  onEditTemplate,
  mode,
  handleDownload,
  onShowFile,
  onDeleteAttachment,
}) {
  const [sortConfig, setSortConfig] = useState({
    key: "template",
    direction: "asc",
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedRows = useMemo(() => {
    const getValue =
      mode === "templates" ? getTemplateValue : getAttachmentValue;

    return [...rows].sort((a, b) => {
      const firstValue = getValue(a, sortConfig.key);
      const secondValue = getValue(b, sortConfig.key);

      if (typeof firstValue === "number" && typeof secondValue === "number") {
        return sortConfig.direction === "asc"
          ? firstValue - secondValue
          : secondValue - firstValue;
      }

      return sortConfig.direction === "asc"
        ? String(firstValue).localeCompare(String(secondValue))
        : String(secondValue).localeCompare(String(firstValue));
    });
  }, [rows, mode, sortConfig]);

  const attachmentColumns = useMemo(() => getAttachmentColumns(rows), [rows]);

  return (
    <div className={styles.templatesTableContainer}>
      <div
        className={styles.templatesTable}
        style={
          mode === "attachments"
            ? { "--templates-table-columns": attachmentColumns }
            : undefined
        }
      >
        {mode === "templates" && (
          <div className={styles.headerContainer}>
            <div className={styles.headerBody}>
              <div
                className={styles.headerGrid}
                style={{
                  "--templates-table-columns": TEMPLATE_SUMMARY_BODY_COLUMNS,
                }}
              >
                <HeaderCell
                  column="1"
                  sortKey="template"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Template
                </HeaderCell>
                <HeaderCell
                  column="4"
                  sortKey="account"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Cuenta
                </HeaderCell>
                <HeaderCell
                  column="8"
                  sortKey="files"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Máx. Mails
                </HeaderCell>
                <HeaderCell
                  column="11"
                  sortKey="responsible"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Responsable
                </HeaderCell>
              </div>
            </div>

            <div className={styles.headerActionSpacer} />
          </div>
        )}

        {mode === "attachments" && (
          <div className={styles.headerContainer}>
            <div className={styles.headerBody}>
              <div className={styles.headerGrid}>
                <HeaderCell
                  column="2"
                  sortKey="template"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Nombre
                </HeaderCell>
                <HeaderCell
                  column="3"
                  sortKey="account"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Cuenta
                </HeaderCell>
                <HeaderCell
                  column="5"
                  sortKey="size"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                >
                  Tamaño
                </HeaderCell>
                <HeaderLabel column="7">Acciones</HeaderLabel>
              </div>
            </div>
          </div>
        )}

        {sortedRows.map((row) => (
          <div key={`${mode}-${row.id}`}>
            {mode === "templates" ? (
              <TemplateRowCard
                template={getTemplateName(row) || "-"}
                brand={row?.account?.name ?? "-"}
                brandColor={row?.account?.color}
                files={String(getTemplateFilesCount(row))}
                ownerName={getOwnerName(row)}
                onAction={() => onSelectTemplate(row)}
                actionIcon={<Vector9Icon />}
                onRowClick={onEditTemplate ? () => onEditTemplate(row) : undefined}
              />
            ) : (
              <AttachmentRowCard
                name={row?.name ?? "-"}
                account={row?.account?.name}
                accountColor={row?.account?.color}
                size={formatFileSize(row?.size)}
                onDownload={() => handleDownload(row?.file)}
                onShowFile={() => onShowFile(row?.file)}
                onDelete={() => onDeleteAttachment?.(row)}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default TemplatesTable;
